import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { toast as sonnerToast } from 'sonner';
import { Eye, EyeOff, MapPin, User, Mail, Phone, Lock, Home, Building2, Hash } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const ADMIN_EMAIL = 'rishnasing@gmail.com';

const Field = ({ id, label, icon: Icon, required = false, ...props }: any) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-sm font-medium flex items-center gap-1">
      {label} {required && <span className="text-destructive">*</span>}
    </Label>
    <div className="relative">
      {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />}
      <Input id={id} className={Icon ? 'pl-9' : ''} required={required} {...props} />
    </div>
  </div>
);

const AuthPage = () => {
  const [tab, setTab] = useState<'login' | 'signup'>('login');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showLoginPwd, setShowLoginPwd] = useState(false);
  const [showSignupPwd, setShowSignupPwd] = useState(false);
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupName, setSignupName] = useState('');
  const [signupPhone, setSignupPhone] = useState('');
  const [signupAddress, setSignupAddress] = useState('');
  const [signupCity, setSignupCity] = useState('');
  const [signupState, setSignupState] = useState('');
  const [signupPostalCode, setSignupPostalCode] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [showForgot, setShowForgot] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { signIn, signUp } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const isAdmin = loginEmail.toLowerCase().trim() === ADMIN_EMAIL.toLowerCase();
      const { error } = await signIn(loginEmail, loginPassword);
      if (error) {
        toast({ title: 'Login failed', description: error.message, variant: 'destructive' });
        return;
      }
      toast({ title: isAdmin ? 'Welcome back, Admin!' : 'Welcome back!', description: 'Redirecting...' });
      setTimeout(() => navigate(isAdmin ? '/admin' : '/my-account'), 500);
    } catch {
      toast({ title: 'Login error', description: 'An unexpected error occurred.', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signupAddress.trim() || !signupCity.trim() || !signupState.trim() || !signupPostalCode.trim()) {
      toast({ title: 'Address required', description: 'Please fill in your complete delivery address.', variant: 'destructive' });
      return;
    }
    setIsLoading(true);
    const { error } = await signUp(signupEmail, signupPassword, {
      full_name: signupName, phone: signupPhone,
      address: signupAddress, city: signupCity, state: signupState, postal_code: signupPostalCode,
    });
    setIsLoading(false);
    if (error) {
      toast({ title: 'Signup failed', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Account created!', description: 'Please check your email to verify your account.' });
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel - branding */}
      <div className="hidden lg:flex lg:w-2/5 bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 text-white flex-col justify-between p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-64 h-64 bg-cyan-400 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-80 h-80 bg-blue-400 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-12">
            <img src="/logo.png" alt="Debnath Telecom" className="h-12 w-12 object-contain" />
            <div>
              <p className="font-heading font-bold text-lg">Debnath Telecom</p>
              <p className="text-cyan-400 text-xs">& Variety Store</p>
            </div>
          </div>
          <h2 className="font-heading text-3xl font-extrabold mb-4 leading-tight">
            Your Trusted<br />
            <span className="text-cyan-400">Electronics Store</span>
          </h2>
          <p className="text-slate-300 text-sm leading-relaxed mb-8">
            Shop genuine smartphones, TVs, and accessories with full warranty and best prices.
          </p>
          <div className="space-y-3">
            {['100% Genuine Products', 'Fast Delivery Across India', 'Full Manufacturer Warranty', 'Best Price Guarantee'].map(f => (
              <div key={f} className="flex items-center gap-2 text-sm text-slate-300">
                <div className="h-1.5 w-1.5 rounded-full bg-cyan-400" />
                {f}
              </div>
            ))}
          </div>
        </div>
        <p className="relative z-10 text-xs text-slate-500">© 2025 Debnath Telecom. All rights reserved.</p>
      </div>

      {/* Right panel - form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-background overflow-y-auto">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <img src="/logo.png" alt="" className="h-9 w-9 object-contain" />
            <div>
              <p className="font-heading font-bold text-sm">Debnath Telecom</p>
              <p className="text-xs text-muted-foreground">& Variety Store</p>
            </div>
          </div>

          {/* Tab switcher */}
          <div className="flex bg-muted rounded-xl p-1 mb-8">
            {(['login', 'signup'] as const).map(t => (
              <button key={t} onClick={() => { setTab(t); setShowForgot(false); }}
                className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all ${tab === t ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
                {t === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            {tab === 'login' ? (
              <motion.div key="login" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} transition={{ duration: 0.2 }}>
                <div className="mb-6">
                  <h1 className="font-heading text-2xl font-bold">Welcome back</h1>
                  <p className="text-sm text-muted-foreground mt-1">Sign in to your account to continue</p>
                </div>

                {!showForgot ? (
                  <form onSubmit={handleLogin} className="space-y-4">
                    <Field id="login-email" label="Email Address" icon={Mail} type="email" value={loginEmail} onChange={(e: any) => setLoginEmail(e.target.value)} placeholder="you@example.com" required />
                    <div className="space-y-1.5">
                      <Label htmlFor="login-pwd" className="text-sm font-medium flex items-center gap-1">Password <span className="text-destructive">*</span></Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input id="login-pwd" type={showLoginPwd ? 'text' : 'password'} className="pl-9 pr-10" value={loginPassword} onChange={e => setLoginPassword(e.target.value)} required />
                        <button type="button" onClick={() => setShowLoginPwd(p => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                          {showLoginPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <button type="button" onClick={() => setShowForgot(true)} className="text-xs text-primary hover:underline">Forgot password?</button>
                    </div>
                    <Button type="submit" className="w-full h-11 font-semibold" disabled={isLoading}>
                      {isLoading ? 'Signing in...' : 'Sign In'}
                    </Button>
                  </form>
                ) : (
                  <form onSubmit={async (e) => {
                    e.preventDefault(); setIsLoading(true);
                    const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, { redirectTo: `${window.location.origin}/reset-password` });
                    setIsLoading(false);
                    if (error) toast({ title: 'Error', description: error.message, variant: 'destructive' });
                    else { sonnerToast.success('Reset link sent! Check your email.'); setShowForgot(false); }
                  }} className="space-y-4">
                    <div className="p-4 bg-muted/50 rounded-xl text-sm text-muted-foreground">Enter your email to receive a password reset link.</div>
                    <Field id="reset-email" label="Email Address" icon={Mail} type="email" value={resetEmail} onChange={(e: any) => setResetEmail(e.target.value)} placeholder="you@example.com" required />
                    <div className="flex gap-2">
                      <Button type="submit" className="flex-1" disabled={isLoading}>{isLoading ? 'Sending...' : 'Send Reset Link'}</Button>
                      <Button type="button" variant="outline" onClick={() => setShowForgot(false)}>Cancel</Button>
                    </div>
                  </form>
                )}
              </motion.div>
            ) : (
              <motion.div key="signup" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
                <div className="mb-6">
                  <h1 className="font-heading text-2xl font-bold">Create account</h1>
                  <p className="text-sm text-muted-foreground mt-1">Fill in your details to get started</p>
                </div>

                <form onSubmit={handleSignup} className="space-y-4">
                  {/* Personal Info */}
                  <div className="space-y-3">
                    <Field id="s-name" label="Full Name" icon={User} value={signupName} onChange={(e: any) => setSignupName(e.target.value)} placeholder="Krishna Singh" required />
                    <Field id="s-email" label="Email Address" icon={Mail} type="email" value={signupEmail} onChange={(e: any) => setSignupEmail(e.target.value)} placeholder="you@example.com" required />
                    <Field id="s-phone" label="Phone Number" icon={Phone} type="tel" value={signupPhone} onChange={(e: any) => setSignupPhone(e.target.value)} placeholder="+91 98765 43210" required />
                  </div>

                  {/* Address Section */}
                  <div className="border rounded-xl p-4 space-y-3 bg-muted/20">
                    <div className="flex items-center gap-2 mb-1">
                      <MapPin className="h-4 w-4 text-primary" />
                      <p className="text-sm font-semibold">Delivery Address <span className="text-destructive">*</span></p>
                      <span className="text-xs text-muted-foreground ml-auto">Required for delivery</span>
                    </div>
                    <Field id="s-address" label="Street Address" icon={Home} value={signupAddress} onChange={(e: any) => setSignupAddress(e.target.value)} placeholder="House no., Street, Area" required />
                    <div className="grid grid-cols-2 gap-3">
                      <Field id="s-city" label="City" icon={Building2} value={signupCity} onChange={(e: any) => setSignupCity(e.target.value)} placeholder="Kharagpur" required />
                      <Field id="s-state" label="State" value={signupState} onChange={(e: any) => setSignupState(e.target.value)} placeholder="West Bengal" required />
                    </div>
                    <Field id="s-pin" label="PIN Code" icon={Hash} value={signupPostalCode} onChange={(e: any) => setSignupPostalCode(e.target.value)} placeholder="721301" required pattern="[0-9]{6}" />
                  </div>

                  {/* Password */}
                  <div className="space-y-1.5">
                    <Label htmlFor="s-pwd" className="text-sm font-medium flex items-center gap-1">Password <span className="text-destructive">*</span></Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input id="s-pwd" type={showSignupPwd ? 'text' : 'password'} className="pl-9 pr-10" value={signupPassword} onChange={e => setSignupPassword(e.target.value)} required minLength={6} placeholder="Min. 6 characters" />
                      <button type="button" onClick={() => setShowSignupPwd(p => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                        {showSignupPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <Button type="submit" className="w-full h-11 font-semibold" disabled={isLoading}>
                    {isLoading ? 'Creating account...' : 'Create Account'}
                  </Button>
                  <p className="text-xs text-center text-muted-foreground">
                    By creating an account, you agree to our terms of service.
                  </p>
                </form>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="mt-6 text-center">
            <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">← Back to store</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
